/*
  LiveUploadStatus — real-time confirmation that assessment audio is
  actually reaching the server.

  Renders during recording (and while there are still pending uploads
  after stopping) with:
    - Big segment counter — how many chunks the browser has saved
    - Server-verified count (polls GET /sessions/{id} every 10s during
      recording so the therapist can be sure the backend agrees)
    - Health tone: green when everything's flushed, amber when queue
      grows or verification lags, red when segments have failed
    - Last saved timestamp (human-friendly "12s ago" etc.)
*/
import { useCallback, useEffect, useState } from "react";
import { useRecording } from "@/lib/recording";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, HardDriveUpload, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";

function timeAgo(ts) {
  if (!ts) return "—";
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
}

export default function LiveUploadStatus() {
  const rec = useRecording();
  const [serverSegments, setServerSegments] = useState(null);
  const [serverBytes, setServerBytes] = useState(null);
  const [serverError, setServerError] = useState(false);
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now()); // re-render the "N seconds ago" every second

  const active = rec.status === "recording" || rec.status === "stopping"
    || rec.status === "stopped" || rec.pendingUploads > 0;

  const sessionId = rec.session?.id;

  const verify = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await api.get(`/soap-notes/sessions/${sessionId}`);
      setServerSegments(r.data?.audio?.segments ?? null);
      setServerBytes(r.data?.audio?.bytes ?? null);
      setServerError(false);
      setLastVerifiedAt(Date.now());
    } catch {
      setServerError(true);
    }
  }, [sessionId]);

  // Verify immediately on mount, then every 10s while active. Also whenever
  // uploadedCount changes (fast re-verification after each upload).
  useEffect(() => {
    if (!active || !sessionId) return;
    verify();
    const id = setInterval(verify, 10000);
    return () => clearInterval(id);
  }, [active, sessionId, verify]);

  useEffect(() => {
    if (rec.uploadedCount > 0 && sessionId && active) {
      // Debounce - only verify at most every 3s after each upload.
      const t = setTimeout(verify, 1500);
      return () => clearTimeout(t);
    }
  }, [rec.uploadedCount, sessionId, active, verify]);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!active || !sessionId) return null;

  // Compute the health tone. Prefer server-verified count where available.
  const uploaded = rec.uploadedCount;
  const pending = rec.pendingUploads;
  const failed = rec.failedSegments || 0;
  const clientTotal = rec.segmentsRecorded || 0;

  let tone = "ok";
  let toneCopy = "All caught up";
  if (failed > 0) {
    tone = "err";
    toneCopy = `${failed} segment${failed === 1 ? "" : "s"} failed to upload`;
  } else if (serverError) {
    tone = "warn";
    toneCopy = "Can't reach server to verify — audio still safe locally";
  } else if (pending > 0) {
    tone = "warn";
    toneCopy = `${pending} chunk${pending === 1 ? "" : "s"} uploading`;
  } else if (serverSegments != null && uploaded > 0 && serverSegments < uploaded) {
    tone = "warn";
    toneCopy = "Waiting on server confirmation";
  } else if (uploaded > 0) {
    tone = "ok";
    toneCopy = "All saved server-side";
  } else {
    tone = "neutral";
    toneCopy = "Waiting for first segment";
  }

  const toneClasses = {
    ok:      "border-emerald-300 bg-emerald-50 text-emerald-900",
    warn:    "border-amber-300 bg-amber-50 text-amber-900",
    err:     "border-red-300 bg-red-50 text-red-900",
    neutral: "border-zinc-200 bg-zinc-50 text-zinc-700",
  }[tone];

  const Icon = tone === "ok" ? CheckCircle2
    : tone === "err" ? AlertTriangle
    : tone === "warn" && serverError ? WifiOff
    : HardDriveUpload;

  return (
    <div
      className={cn("rounded-md border p-3", toneClasses)}
      data-testid="soap-live-upload-status"
    >
      <div className="flex items-center gap-3">
        <Icon className={cn(
          "h-6 w-6 shrink-0",
          tone === "warn" && "animate-pulse",
        )} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] opacity-80">
            Live upload status
          </div>
          <div className="mt-0.5 text-sm font-medium">{toneCopy}</div>
        </div>
        <div
          className="text-right shrink-0"
          data-testid="soap-live-upload-counter"
        >
          <div className="text-2xl font-mono-num tabular-nums leading-none">
            {uploaded}
          </div>
          <div className="text-2xs uppercase tracking-wider opacity-70">
            saved
          </div>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-2xs">
        <Stat label="Recorded" value={clientTotal} testId="soap-stat-recorded" />
        <Stat label="Queued" value={pending} accent={pending > 0} testId="soap-stat-queued" />
        <Stat label="Failed" value={failed} accent={failed > 0} testId="soap-stat-failed" />
        <Stat
          label="On server"
          value={serverSegments != null ? serverSegments : "—"}
          testId="soap-stat-server"
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-2xs opacity-70 font-mono-num">
        <span data-testid="soap-live-last-saved">Last saved: {timeAgo(rec.lastUploadAt)}</span>
        <span data-testid="soap-live-last-verified">Server verified: {timeAgo(lastVerifiedAt)}</span>
        {serverBytes != null && (
          <span>{(serverBytes / 1024 / 1024).toFixed(1)} MB on disk</span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div
      className={cn(
        "rounded border px-2 py-1 bg-white/50",
        accent ? "border-current" : "border-transparent",
      )}
      data-testid={testId}
    >
      <div className="text-2xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-mono-num tabular-nums leading-tight">{value}</div>
    </div>
  );
}
